import { create } from 'zustand';

/**
 * Most-recently-used ordering for the home Quick Links rail. Opening a link
 * moves it to the front, so the rail — capped at {@link QUICK_LINKS_VISIBLE} —
 * always carries what the member actually uses, and the rest stay one tap away
 * behind "View all". Persisted to localStorage; the `paas_` prefix mirrors it
 * to native Preferences via `durablePrefs`.
 */

const STORAGE_KEY = 'paas_quick_links_recent';

/** Tiles rendered in the rail. Anything beyond this only appears in the sheet. */
export const QUICK_LINKS_VISIBLE = 5;

interface QuickLinksState {
  /** Quick-link ids, most recently opened first. */
  recent: string[];
  promote: (id: string) => void;
}

function load(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    if (Array.isArray(parsed)) return parsed.filter((id): id is string => typeof id === 'string');
  } catch {
    /* storage unavailable / malformed — start with tenant order */
  }
  return [];
}

function save(recent: string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(recent));
  } catch (error) {
    console.warn('[quick-links] persist failed:', error);
  }
}

export const useQuickLinksStore = create<QuickLinksState>((set, get) => ({
  recent: load(),
  promote(id) {
    const recent = [id, ...get().recent.filter((existing) => existing !== id)];
    set({ recent });
    save(recent);
  },
}));

/**
 * Sort quick links by most-recent use: opened links first in the order they
 * were last opened, then everything else in its tenant order (the sort is
 * stable, so untouched links keep their relative positions). Panic is pinned
 * to the front — ordinary use must never push it out of the rail.
 */
export function orderByRecentUse<T extends { id: string; isPanic?: boolean }>(
  links: T[],
  recent: string[],
): T[] {
  const rank = new Map(recent.map((id, index) => [id, index]));
  const score = (link: T) =>
    link.isPanic ? -1 : (rank.get(link.id) ?? Number.MAX_SAFE_INTEGER);

  return [...links].sort((a, b) => score(a) - score(b));
}
